import type { Amount, AmountFees, Currency, Discount, MultiTax, Tax, TaxId } from '@hero/schema';
import { CONFIG } from '../config';
import { computeFeeTotals } from './fees';
import { computeSubtotals } from './subtotals';
import { computeTaxTotals, getMainTax, makeTax, splitInclusiveTax } from './tax';
import { computeRemaining, computeTotal } from './total';
import { roundAmount } from './rounding';

/**
 * Inputs accepted by {@link computeAmountPreview}. Mirrors
 * `AmountServiceData` (`apps/backend/src/app/amount/amount.service.ts`)
 * field-for-field — anything you can pass into `new AmountService(...)`
 * you can pass here.
 */
export interface ComputeAmountInput {
  currency?: Currency;
  /** Items amount BEFORE any discount, fees, or tax (i.e. the load price). */
  itemsOriginalSubtotal?: number;
  /**
   * Legacy fallbacks. The constructor coalesces in this order when
   * `itemsOriginalSubtotal` is absent:
   *   `itemsOriginalSubtotal || itemsSubtotal || subtotal`
   * for `itemsOriginalSubtotal`, and
   *   `itemsSubtotal || itemsOriginalSubtotal || subtotal`
   * for `itemsSubtotal` (note the different ordering — see source).
   * Most callers should just pass `itemsOriginalSubtotal`.
   */
  itemsSubtotal?: number;
  subtotal?: number;
  realizedSubtotal?: number;
  discount?: Partial<Discount>;
  fees?: Partial<AmountFees>;
  /**
   * Pre-built tax block (e.g. when copying from a persisted Amount).
   * Wins over `taxId` when `applied` is non-empty.
   */
  tax?: Partial<MultiTax>;
  /**
   * Convenience: seed a tax stack from a known id when `tax.applied`
   * is empty. Same as calling `addTax(taxId)` on the service.
   */
  taxId?: TaxId;
  /**
   * When provided alongside a positive value, back-derive
   * `itemsOriginalSubtotal` from the tax-inclusive total — same path
   * the `AmountService` constructor runs. Useful when the caller only
   * knows "user-facing total".
   */
  total?: number;
  paid?: number;
  remaining?: number;
}

/**
 * Pure, domain-neutral builder for an {@link Amount}. Same math as
 * `AmountService` so any consumer (orders, invoices, subscriptions,
 * billings, payments, financing, frontend previews, mobile apps)
 * lands on identical numbers without standing up a Nest container or
 * re-implementing the formula.
 *
 * **Hard rule** (enforced by `computeAmountPreview.spec.ts`): the
 * `Amount` produced here MUST equal what `new AmountService(input).amount`
 * produces, field-for-field, for every input the service accepts. If
 * you change one, change the other and re-run the equivalence spec.
 *
 * Anywhere else in the repo that needs to derive a load-price → tax →
 * total preview (reorder modal, edit-price drawer, publish-flow live
 * calc display, mobile order/invoice cards, subscription preview)
 * should call THIS function, not roll its own.
 *
 * @see apps/backend/src/app/amount/amount.service.ts
 * @see libs/shared/core/src/amount/README.md
 */
export function computeAmountPreview(input: ComputeAmountInput = {}): Amount {
  // ----------------------------------------------------------------
  // Phase 1 — apply the same defaults the AmountService constructor
  //          uses, in the same order, so missing fields produce
  //          identical seed values.
  // ----------------------------------------------------------------
  const currency: Currency = input.currency ?? CONFIG.price.currency;
  const discount: Discount = {
    amount: input.discount?.amount ?? 0,
    percentage: input.discount?.percentage ?? 0,
  };
  const seedFees: AmountFees = {
    applied: input.fees?.applied ?? [],
    all: input.fees?.all ?? 0,
    added: input.fees?.added ?? 0,
    realized: input.fees?.realized ?? 0,
    subtracted: input.fees?.subtracted ?? 0,
    total: input.fees?.total ?? 0,
  };

  // Same coalescing ladder as the constructor — order matters because
  // the legacy callers pass `subtotal` as a synonym for "load price".
  const originalSubtotalIn = input.itemsOriginalSubtotal ?? 0;
  const itemsSubtotalIn = input.itemsSubtotal ?? 0;
  const subtotalIn = input.subtotal ?? 0;
  let itemsOriginalSubtotal =
    originalSubtotalIn || itemsSubtotalIn || subtotalIn;

  const paid = input.paid ?? 0;
  const total = input.total ?? 0;

  // ----------------------------------------------------------------
  // Phase 2 — seed the tax stack. `applied` wins; if empty and a
  //          `taxId` was supplied, push a fresh entry (same as
  //          `addTax` in the service).
  // ----------------------------------------------------------------
  const seedTaxApplied: Tax[] =
    input.tax?.applied && input.tax.applied.length > 0
      ? [...input.tax.applied]
      : input.taxId
      ? [makeTax(input.taxId)]
      : [];

  // ----------------------------------------------------------------
  // Phase 3 — back-derive `itemsOriginalSubtotal` from `total` when
  //          the caller only supplied the headline number. Mirrors
  //          the constructor's "if total > 0" branch exactly,
  //          including the (intentional) overwrite of any
  //          `itemsOriginalSubtotal` that was also supplied.
  // ----------------------------------------------------------------
  if (total > 0) {
    const mainTax = getMainTax(seedTaxApplied);
    if (mainTax) {
      const split = splitInclusiveTax(total, mainTax.percentage);
      itemsOriginalSubtotal = split.subtotal - seedFees.total - discount.amount;
    } else {
      itemsOriginalSubtotal = total - seedFees.total - discount.amount;
    }
  }

  // ----------------------------------------------------------------
  // Phase 4 — calcAll. Order matches AmountService.calcAll() exactly:
  //   1. fees      → derive rollups from applied[]
  //   2. subtotals → itemsSubtotal / subtotal / realizedSubtotal
  //   3. tax       → per-tax amounts, plus tax.total
  //   4. total     → subtotal + tax.total
  //   5. remaining → total - paid (or 0 when over-paid)
  //   6. round     → roundAll
  // ----------------------------------------------------------------
  const fees = computeFeeTotals(seedFees.applied);

  const { itemsSubtotal, subtotal, realizedSubtotal } = computeSubtotals({
    itemsOriginalSubtotal,
    discount,
    fees,
  });

  const tax = computeTaxTotals(seedTaxApplied, subtotal);
  const newTotal = computeTotal(subtotal, tax.total);
  const remaining = computeRemaining(newTotal, paid);

  const built: Amount = {
    currency,
    total: newTotal,
    subtotal,
    itemsSubtotal,
    itemsOriginalSubtotal,
    realizedSubtotal,
    paid,
    remaining,
    discount,
    fees,
    tax,
  };

  return roundAmount(built);
}
